import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CaseType,
  Project,
  ProjectDetailResponse,
  ProjectsListResponse,
  ProjectStatus,
} from '@stfors/shared-types';
import { api } from '../client';

export interface CreateProjectInput {
  client_id: number;
  /** Optional — when omitted the server allocates a non-disclosing
   *  random case number of the form STF-YYYY-XXXXXXXX. */
  case_number?: string;
  case_type: CaseType;
  title: string;
  description?: string;
  status?: ProjectStatus;
  priority?: 1 | 2 | 3 | 4 | 5;
  billing_type: string; // admin-managed 'billing_type' lookup (0037)
  flat_fee_amount?: string;
  is_billable?: boolean;
  jurisdiction?: string;
  /** Display-name snapshot of the lead attorney. Written alongside
   *  `rep_person_id` so the readers that predate it keep working. */
  lead_attorney?: string;
  /** The client's representative for this matter (migration 0054). */
  rep_organization_id?: number;
  rep_person_id?: number;
}

export interface ProjectListFilters {
  client_id?: number;
}

export function useProjects(filters: ProjectListFilters = {}) {
  const qs = filters.client_id !== undefined ? `?client_id=${filters.client_id}` : '';
  return useQuery({
    queryKey: ['projects', { client_id: filters.client_id ?? null }],
    queryFn: ({ signal }) =>
      api.get<ProjectsListResponse>(`/api/projects${qs}`, signal),
  });
}

export function useProjectDetail(id: number | null) {
  return useQuery({
    queryKey: ['projects', { id }],
    queryFn: ({ signal }) =>
      api.get<ProjectDetailResponse>(`/api/projects/${id}`, signal),
    enabled: id !== null,
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProjectInput) =>
      api.post<{ project: Project }>('/api/projects', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export interface UpdateProjectInput {
  status?: ProjectStatus;
  /** Nullable: clearing the attorney clears the display-name snapshot too. */
  lead_attorney?: string | null;
  /** This matter's representative; null inherits the client's default (0054). */
  rep_organization_id?: number | null;
  rep_person_id?: number | null;
  bill_to_override?: string | null;
  priority?: 1 | 2 | 3 | 4 | 5;
  title?: string;
  description?: string;
  jurisdiction?: string;
  court_date?: number | null;
  target_end_date?: number | null;
}

export function useUpdateProject(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateProjectInput) =>
      api.patch<{ project: Project }>(`/api/projects/${id}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useArchiveProject(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ project: Project }>(`/api/projects/${id}/archive`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}
